'use client';

import React, { useState, useRef, useCallback } from 'react';
import { X, Save, Loader2, UserCheck, User, Mail, Phone, Building2, Ticket, CheckCircle2, AlertTriangle, Camera, ScanLine, RefreshCw, ImageIcon } from 'lucide-react';
import { entriesService } from '@/lib/services/entriesService';
import { emailSettingsService, applyBuilderTemplate } from '@/lib/services/emailSettingsService';
import { toast } from 'sonner';

interface SalesRepEntryModalProps {
  onClose: () => void;
}

const PRODUCTS = [
  { id: 'IDE', label: 'IDE' },
  { id: '3TL', label: '3TL' },
  { id: 'Access Manager', label: 'Access Manager' },
];

const REPS = ['Dominique', 'Saket', 'Chandan', 'Other'];

/** Parse raw OCR text and extract name, email, phone, company */
function parseBusinessCard(text: string): { name: string; email: string; phone: string; company: string } {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

  const emailMatch = text.match(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/);
  const email = emailMatch ? emailMatch[0] : '';

  const phoneMatch = text.match(/(\+?[\d\s\-().]{7,20})/);
  const phone = phoneMatch ? phoneMatch[0].trim() : '';

  // Company heuristics: line with Ltd, Inc, Corp, LLC, Group, Co., Technologies, Solutions, etc.
  const companyKeywords = /\b(Ltd|Limited|Inc|Corp|Corporation|LLC|LLP|Group|Co\.|Technologies|Solutions|Services|Consulting|Agency|Studio|Labs|Systems)\b/i;
  const companyLine = lines.find((l) => companyKeywords.test(l) && !emailMatch?.[0].includes(l));
  const company = companyLine ?? '';

  // Name heuristic: first line that is not email, phone, company, and looks like a name (2 words, mostly alpha)
  const nameLine = lines.find((l) => {
    if (email && l.includes(email)) return false;
    if (phone && l.includes(phone.replace(/\s/g, ''))) return false;
    if (company && l === company) return false;
    const words = l.split(/\s+/);
    return words.length >= 2 && words.length <= 5 && /^[A-Za-z\s.\-']+$/.test(l);
  });
  const name = nameLine ?? '';

  return { name, email, phone, company };
}

export default function SalesRepEntryModal({ onClose }: SalesRepEntryModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [repName, setRepName] = useState('');
  const [trialEapOptin, setTrialEapOptin] = useState(false);
  const [raffleOptin, setRaffleOptin] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedRaffleCode, setSavedRaffleCode] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Business card camera / OCR state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [cardImageBlob, setCardImageBlob] = useState<Blob | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [cameraError, setCameraError] = useState('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleProduct = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((p) => p !== productId) : [...prev, productId]
    );
  };

  // ── Camera helpers ──────────────────────────────────────────────────────────

  const startCamera = useCallback(async () => {
    setScanError('');
    setCameraError('');
    setCapturedImage(null);
    setCardImageBlob(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setCameraStream(stream);
      setShowCamera(true);
      // Attach stream to video element after state update
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (err: any) {
      setCameraError('Camera access denied or unavailable. Use the upload option instead.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((t) => t.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  }, [cameraStream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setCapturedImage(dataUrl);
    canvas.toBlob((blob) => { if (blob) setCardImageBlob(blob); }, 'image/jpeg', 0.92);
    stopCamera();
  }, [stopCamera]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanError('');
    setCapturedImage(null);
    setCardImageBlob(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setCapturedImage(dataUrl);
      setCardImageBlob(file);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const runOCR = useCallback(async () => {
    if (!capturedImage) return;
    setIsScanning(true);
    setScanError('');
    try {
      // Dynamic import to avoid SSR issues
      const Tesseract = (await import('tesseract.js')).default;
      const { data: { text } } = await Tesseract.recognize(capturedImage, 'eng', {
        logger: () => {},
      });
      const parsed = parseBusinessCard(text);
      if (parsed.name) setName(parsed.name);
      if (parsed.email) setEmail(parsed.email);
      if (parsed.phone) setPhone(parsed.phone);
      if (parsed.company) setCompany(parsed.company);
      if (!parsed.name && !parsed.email && !parsed.phone && !parsed.company) {
        setScanError('Could not extract details. Please fill in manually or retake the photo.');
      } else {
        toast.success('Business card scanned! Please review and correct the pre-filled fields.');
      }
    } catch (err: any) {
      setScanError('OCR failed. Please fill in the details manually.');
    } finally {
      setIsScanning(false);
    }
  }, [capturedImage]);

  const retakePhoto = () => {
    setCapturedImage(null);
    setCardImageBlob(null);
    setScanError('');
    startCamera();
  };

  const discardCard = () => {
    setCapturedImage(null);
    setCardImageBlob(null);
    setScanError('');
    stopCamera();
  };

  // ── Validation & save ───────────────────────────────────────────────────────

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!repName) newErrors.repName = 'Please select your name';
    if (!name.trim() || name.trim().length < 2) newErrors.name = 'Full name is required (min 2 chars)';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) newErrors.email = 'Enter a valid email address';
    if (!phone.trim() || phone.trim().length < 7) newErrors.phone = 'Enter a valid phone number';
    if (!company.trim() || company.trim().length < 2) newErrors.company = 'Company name is required';
    return newErrors;
  };

  const sendRaffleConfirmationEmail = async (entryId: string, raffleCode: string) => {
    try {
      const settings = await emailSettingsService.getSettings();
      const fromName = settings?.fromName || 'Event Team';

      const raffleSubjectRaw = settings?.raffleSubject || `Your Raffle Entry — Code: {{raffle_code}}`;
      const subject = raffleSubjectRaw.replace(/{{raffle_code}}/g, raffleCode);

      let raffleHtml: string;

      if (settings?.raffleHtmlTemplate) {
        // Use admin-configured raffle template from the drag-and-drop builder.
        // applyBuilderTemplate wraps it in a responsive shell and substitutes all
        // {{template_variables}} in one pass.
        raffleHtml = applyBuilderTemplate(settings.raffleHtmlTemplate, {
          playerName: name.trim(),
          email: email.trim(),
          company: company.trim(),
          guess: 0,
          raffleCode,
          repName,
        });
      } else {
        // Built-in default raffle email (no builder template configured)
        raffleHtml = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8" /><meta name="viewport" content="width=device-width,initial-scale=1.0" /></head>
<body style="margin:0;padding:0;background:#f9fafb;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;">
  <tr>
    <td align="center" style="padding:24px 16px;">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.06);">
        <tr>
          <td style="padding:32px 24px;">
            <h2 style="color:#7c3aed;margin-bottom:8px;">&#127903;&#65039; You're in the Raffle!</h2>
            <p style="color:#374151;margin-bottom:16px;">Hi ${name.trim()},</p>
            <p style="color:#374151;margin-bottom:16px;">
              You've been entered into our raffle draw by ${repName}. Here is your unique raffle code:
            </p>
            <div style="background:#f5f3ff;border:2px solid #c4b5fd;border-radius:12px;padding:20px;text-align:center;margin-bottom:20px;">
              <p style="font-size:28px;font-weight:900;color:#7c3aed;letter-spacing:6px;margin:0;">${raffleCode}</p>
              <p style="font-size:12px;color:#6d28d9;margin-top:8px;">Your Raffle Code</p>
            </div>
            <p style="color:#6b7280;font-size:14px;">
              Keep this code safe — winners will be announced on our LinkedIn channel after the event. Good luck! &#127808;
            </p>
            <hr style="border:none;border-top:1px solid #e5e7eb;margin:20px 0;" />
            <p style="color:#9ca3af;font-size:12px;">Entered by: ${repName} &middot; ${company.trim()}</p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
      }

      await emailSettingsService.sendConfirmationEmail({
        to: email.trim(),
        fromName,
        subject,
        htmlBody: raffleHtml,
        entryId,
        siteUrl: process.env.NEXT_PUBLIC_SITE_URL,
      });
    } catch (err: any) {
      setEmailError(`Entry saved, but raffle email failed: ${err.message ?? 'Unknown error'}`);
    }
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setEmailError(null);
    setIsSaving(true);
    try {
      const emailExists = await entriesService.checkEmailExists(email.trim());
      if (emailExists) {
        setErrors({ email: 'This email address has already been used to enter.' });
        toast.error('This email address has already been entered.');
        setIsSaving(false);
        return;
      }

      const result = await entriesService.createEntry({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        company: company.trim(),
        trialEapOptin,
        raffleOptin,
      });

      const entryId = result.id;
      const raffleCode = result.raffleCode;

      await entriesService.saveSalesRepData(entryId, {
        repName,
        productInterest: selectedProducts,
        notes,
      });

      // Upload business card image if captured
      if (cardImageBlob) {
        try {
          await entriesService.uploadPlayerPhoto(entryId, cardImageBlob);
        } catch {
          // Non-fatal — entry is saved even if photo upload fails
        }
      }

      if (raffleOptin && raffleCode) {
        setSavedRaffleCode(raffleCode);
        await sendRaffleConfirmationEmail(entryId, raffleCode);
      }

      setSaved(true);
      toast.success(raffleOptin ? '✅ Entry saved & raffle confirmation email sent!' : 'Entry saved successfully!');
      setTimeout(() => onClose(), 2200);
    } catch (err: any) {
      toast.error(`Failed to save entry: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <UserCheck size={20} className="text-indigo-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-base">Sales Rep Entry</h3>
              <p className="text-xs text-gray-400">Capture player details manually</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Email error banner */}
          {emailError && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
              <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
              <span>{emailError}</span>
            </div>
          )}

          {/* Saved confirmation with raffle code */}
          {saved && savedRaffleCode && (
            <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl px-4 py-3">
              <CheckCircle2 size={18} className="text-purple-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-purple-700">Raffle entry confirmed!</p>
                <p className="text-xs text-purple-500">Code: <span className="font-extrabold tracking-widest">{savedRaffleCode}</span> — confirmation email sent</p>
              </div>
            </div>
          )}

          {/* ── Business Card Scanner ─────────────────────────────────────── */}
          <div className="rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ScanLine size={16} className="text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-700">Business Card Scanner</span>
                <span className="text-xs text-indigo-400 font-normal">(optional)</span>
              </div>
              {capturedImage && (
                <button onClick={discardCard} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                  Discard
                </button>
              )}
            </div>

            {/* Camera error */}
            {cameraError && (
              <p className="text-xs text-red-500 mb-2">{cameraError}</p>
            )}

            {/* Live camera view */}
            {showCamera && !capturedImage && (
              <div className="space-y-2">
                <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                  <div className="absolute inset-0 border-2 border-white/30 rounded-xl pointer-events-none" />
                  <div className="absolute inset-x-4 inset-y-6 border-2 border-white/60 rounded-lg pointer-events-none" />
                </div>
                <div className="flex gap-2">
                  <button onClick={capturePhoto}
                    className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors">
                    <Camera size={15} /> Capture
                  </button>
                  <button onClick={stopCamera}
                    className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Captured image preview */}
            {capturedImage && (
              <div className="space-y-3">
                <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-video">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={capturedImage} alt="Captured business card" className="w-full h-full object-contain" />
                  {isScanning && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-2">
                      <Loader2 size={24} className="text-white animate-spin" />
                      <p className="text-white text-xs font-medium">Scanning card…</p>
                    </div>
                  )}
                </div>
                {scanError && (
                  <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">{scanError}</p>
                )}
                <div className="flex gap-2">
                  <button onClick={runOCR} disabled={isScanning}
                    className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors">
                    {isScanning ? <><Loader2 size={14} className="animate-spin" /> Scanning…</> : <><ScanLine size={14} /> Extract Details</>}
                  </button>
                  <button onClick={retakePhoto} disabled={isScanning}
                    className="px-3 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-1.5">
                    <RefreshCw size={13} /> Retake
                  </button>
                </div>
              </div>
            )}

            {/* Idle state — show action buttons */}
            {!showCamera && !capturedImage && (
              <div className="flex gap-2">
                <button onClick={startCamera}
                  className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                  <Camera size={15} /> Take Photo
                </button>
                <button onClick={() => fileInputRef.current?.click()}
                  className="flex-1 py-2.5 border-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
                  <ImageIcon size={15} /> Upload Image
                </button>
              </div>
            )}

            {/* Hidden file input */}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />

            {/* Hidden canvas for capture */}
            <canvas ref={canvasRef} className="hidden" />

            <p className="text-xs text-indigo-400 mt-2 text-center">
              Scan a business card to auto-fill the form below
            </p>
          </div>

          {/* Sales Rep Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Sales Rep Name <span className="text-red-400">*</span>
            </label>
            <select
              value={repName}
              onChange={(e) => { setRepName(e.target.value); setErrors((prev) => ({ ...prev, repName: '' })); }}
              className={`w-full px-3 py-2.5 rounded-xl border text-sm font-medium text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 ${errors.repName ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
            >
              <option value="">Select your name...</option>
              {REPS.map((rep) => <option key={rep} value={rep}>{rep}</option>)}
            </select>
            {errors.repName && <p className="mt-1 text-xs text-red-500 font-medium">{errors.repName}</p>}
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Player Details</p>

            {/* Name */}
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name <span className="text-red-400">*</span></label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={name} onChange={(e) => { setName(e.target.value); setErrors((prev) => ({ ...prev, name: '' })); }} placeholder="Sarah Mitchell"
                  className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors outline-none focus:ring-2 focus:ring-indigo-400/30 ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:border-indigo-400 focus:bg-white'}`} />
              </div>
              {errors.name && <p className="mt-1 text-xs text-red-500 font-medium">{errors.name}</p>}
            </div>

            {/* Email */}
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address <span className="text-red-400">*</span></label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErrors((prev) => ({ ...prev, email: '' })); }} placeholder="sarah@company.com"
                  className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors outline-none focus:ring-2 focus:ring-indigo-400/30 ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:border-indigo-400 focus:bg-white'}`} />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-500 font-medium">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number <span className="text-red-400">*</span></label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setErrors((prev) => ({ ...prev, phone: '' })); }} placeholder="+1 (555) 234-5678"
                  className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors outline-none focus:ring-2 focus:ring-indigo-400/30 ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:border-indigo-400 focus:bg-white'}`} />
              </div>
              {errors.phone && <p className="mt-1 text-xs text-red-500 font-medium">{errors.phone}</p>}
            </div>

            {/* Company */}
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company / Organisation <span className="text-red-400">*</span></label>
              <div className="relative">
                <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={company} onChange={(e) => { setCompany(e.target.value); setErrors((prev) => ({ ...prev, company: '' })); }} placeholder="Acme Corp"
                  className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors outline-none focus:ring-2 focus:ring-indigo-400/30 ${errors.company ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:border-indigo-400 focus:bg-white'}`} />
              </div>
              {errors.company && <p className="mt-1 text-xs text-red-500 font-medium">{errors.company}</p>}
            </div>
          </div>

          {/* Product Interest */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Product Interest <span className="text-gray-400 font-normal">(select all that apply)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {PRODUCTS.map((product) => {
                const isSelected = selectedProducts.includes(product.id);
                return (
                  <button key={product.id} type="button" onClick={() => toggleProduct(product.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-150 ${isSelected ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'}`}>
                    {isSelected && <span className="mr-1">✓</span>}
                    {product.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any notes about this player..." rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 placeholder:text-gray-400" />
          </div>

          {/* Raffle Opt-in */}
          <div className="p-3 rounded-xl border border-purple-200 bg-purple-50">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <div className="relative flex-shrink-0 mt-0.5">
                <input type="checkbox" checked={raffleOptin} onChange={(e) => setRaffleOptin(e.target.checked)} className="absolute opacity-0 w-5 h-5 cursor-pointer" style={{ zIndex: 1 }} />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all pointer-events-none ${raffleOptin ? 'bg-purple-600 border-purple-600' : 'border-gray-300 bg-white'}`}>
                  {raffleOptin && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-purple-700 mb-0.5 flex items-center gap-1.5">
                  <Ticket size={12} /> Enter into Raffle Draw
                </p>
                <p className="text-xs text-purple-600 leading-relaxed">
                  Player opts in for the raffle draw. A confirmation email with their raffle code will be sent automatically.
                </p>
              </div>
            </label>
          </div>

          {/* Trial / EAP Opt-in */}
          <div className="p-3 rounded-xl border border-indigo-200 bg-indigo-50">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <div className="relative flex-shrink-0 mt-0.5">
                <input type="checkbox" checked={trialEapOptin} onChange={(e) => setTrialEapOptin(e.target.checked)} className="absolute opacity-0 w-5 h-5 cursor-pointer" style={{ zIndex: 1 }} />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all pointer-events-none ${trialEapOptin ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'}`}>
                  {trialEapOptin && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-xs font-semibold text-indigo-700 mb-0.5">🚀 Free Trial / Early Access Program (EAP)</p>
                <p className="text-xs text-indigo-600 leading-relaxed">Player is interested in a free trial or the Early Access Program.</p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-3 rounded-2xl border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving || saved}
              className={`flex-1 py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all duration-150 ${saved ? 'bg-green-500 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md disabled:opacity-60'}`}>
              {saved ? <>✓ Entry Saved!</> : isSaving ? <><Loader2 size={16} className="animate-spin" />Saving...</> : <><Save size={16} />Save Entry</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
